import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { KeycloakService } from 'keycloak-angular';
import {
  extractHackathonMyWorkflowsListFromResponse,
  HackathonMyWorkflowRow,
  HackathonRunDetailsResponse,
  HackathonSubmitError,
  HackathonSubmitResponse,
  LeadershipApiService,
  RolesApiService,
  SandboxApiService
} from '@angular-workspace/services';
import { FooterComponent } from '../../../shared/components/footer/footer.component';
import { LoginModalComponent } from '../../../shared/components/login-modal/login-modal.component';
import { ModalComponent } from '../../../shared/components/primeng/modal/modal.component';
import { NhaContentService } from '../../../shared/services/nha-content.service';
import { ToasterService } from '../../../services/toaster.service';
import { ProblemStatement, STATIC_PROBLEM_STATEMENTS } from '../reference-datasets.component';

interface ProblemStatementResourceItem {
  label: string;
  description: string;
  fileUrl: string;
}

@Component({
  selector: 'app-problem-statement-detail',
  standalone: true,
  imports: [CommonModule, RouterModule, FooterComponent, LoginModalComponent, ModalComponent],
  template: `
    <!-- ── Top bar ──────────────────────────────────────────── -->
    <div class="ps-detail__topbar">
      <div class="ps-detail__topbar-inner">
        <div class="ps-detail__topbar-start">
          <button type="button" class="ps-detail__back" (click)="goBack()">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
              stroke-linecap="round" stroke-linejoin="round">
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
            Back to Hackathon
          </button>
          <nav class="ps-detail__crumbs" aria-label="Breadcrumb">
            <a routerLink="/reference" class="ps-detail__breadcrumb">Hackathon</a>
            <span class="ps-detail__breadcrumb-sep">/</span>
            <span class="ps-detail__breadcrumb-current">{{ ps?.tag ?? 'Problem Statement' }}</span>
          </nav>
        </div>
      </div>
    </div>

    <!-- ── Loading skeleton ──────────────────────────────────── -->
    <div class="ps-detail__container" *ngIf="isLoading">
      <div class="ps-detail__skeleton">
        <div class="skel skel--chip"></div>
        <div class="skel skel--h1"></div>
        <div class="skel skel--h1 skel--short"></div>
        <div class="skel skel--body" *ngFor="let n of [1,2,3,4]"></div>
      </div>
    </div>

    <!-- ── Not found ─────────────────────────────────────────── -->
    <div class="ps-detail__container ps-detail__notfound" *ngIf="!isLoading && notFound">
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"
        stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="12" y1="8" x2="12" y2="12"></line>
        <line x1="12" y1="16" x2="12.01" y2="16"></line>
      </svg>
      <h2>Problem statement not found</h2>
      <p>The problem statement you're looking for doesn't exist or has been removed.</p>
      <button type="button" class="ps-detail__cta" (click)="goBack()">Return to Hackathon</button>
    </div>

    <!-- ── Main content ──────────────────────────────────────── -->
    <div class="ps-detail__container" *ngIf="!isLoading && ps">
      <div class="ps-detail__header">
        <div class="ps-detail__header-meta">
          <span class="ps-detail__tag">{{ ps!.tag }}</span>
          <span class="ps-detail__diff" [ngClass]="difficultyClass(ps!.difficulty)">{{ ps!.difficulty }}</span>
          <span class="ps-detail__domain">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="3"></circle>
              <path d="M19.07 4.93a10 10 0 0 1 0 14.14"></path>
              <path d="M4.93 4.93a10 10 0 0 0 0 14.14"></path>
            </svg>
            {{ ps!.domain }}
          </span>
        </div>
        <h1 class="ps-detail__title">{{ ps!.title }}</h1>
      </div>

      <div class="ps-detail__body">
        <div class="ps-detail__content" [ngSwitch]="ps!.id">

          <!-- ══════════════════════════════════════════════════
               PS-01
               ══════════════════════════════════════════════════ -->
          <ng-container *ngSwitchCase="1">

            <div class="ps1-intro">
              <div class="ps1-intro__icon-wrap">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"
                  stroke-linecap="round" stroke-linejoin="round">
                  <rect x="2" y="3" width="20" height="14" rx="2"></rect>
                  <line x1="8" y1="21" x2="16" y2="21"></line>
                  <line x1="12" y1="17" x2="12" y2="21"></line>
                </svg>
              </div>
              <div class="ps1-intro__body">
                <span class="ps1-intro__eyebrow">Problem Statement Title</span>
                <p class="ps1-intro__text">
                  Automatically read mixed quality healthcare documents, extract key data, detect mandatory
                  visual elements (like stamps&nbsp;/&nbsp;signatures), and check compliance with standard
                  treatment/policy rules — then produce an explainable
                  <strong>Pass&nbsp;/&nbsp;Conditional&nbsp;/&nbsp;Fail</strong> decision with reasons
                  based on Standard Treatment Guidelines (STG) provided by NHA for each package code.
                </p>
              </div>
            </div>

            <section class="ps1-card">
              <div class="ps1-card__hd">
                <span class="ps1-card__icon-wrap ps1-card__icon-wrap--amber">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                    stroke-linecap="round" stroke-linejoin="round">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                    <line x1="12" y1="9" x2="12" y2="13"></line>
                    <line x1="12" y1="17" x2="12.01" y2="17"></line>
                  </svg>
                </span>
                <h3 class="ps1-card__title">Core Problem Statement</h3>
              </div>
              <ul class="ps1-list">
                <li>Reads documents even if blurry or in different languages.</li>
                <li>Finds important fields (patient name, diagnosis, procedures, amounts).</li>
                <li>Detects visual cues (hospital stamp, doctor signature, implant stickers, QR/barcodes).</li>
                <li>Checks claims against rules (eligibility, diagnostics, length of stay, package logic).</li>
                <li>Explains decisions with confidence and evidence source.</li>
                <li>Constructs an episode timeline and validates chronology.</li>
                <li>Highlights extra or non-required documents.</li>
              </ul>
            </section>

            <section class="ps1-card">
              <div class="ps1-card__hd">
                <span class="ps1-card__icon-wrap ps1-card__icon-wrap--green">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                    stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                </span>
                <h3 class="ps1-card__title">Expected Solution</h3>
              </div>
              <ul class="ps1-list ps1-list--check">
                <li>Document intake: PDFs/images (low to high quality), multilingual.</li>
                <li>OCR &amp; layout understanding: text + structure (sections / tables / line items).</li>
                <li>Visual element detection: stamps, signatures, implant stickers, QR/barcodes, invoice items.</li>
                <li>Data structuring: standard schema (patient, diagnosis, procedures, dates, costs).</li>
                <li>Rules engine: encode STG/policy checks (eligibility, contraindications, length of stay).</li>
                <li>Explainability: confidence scores + provenance (page number, bounding box, source doc ID).</li>
                <li>Decisioning: Pass / Conditional (needs more info) / Fail with prioritized flags.</li>
              </ul>
            </section>

            <section class="ps1-card">
              <div class="ps1-card__hd">
                <span class="ps1-card__icon-wrap ps1-card__icon-wrap--blue">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                    stroke-linecap="round" stroke-linejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2"></rect>
                    <line x1="3" y1="9" x2="21" y2="9"></line>
                    <line x1="3" y1="15" x2="21" y2="15"></line>
                    <line x1="9" y1="3" x2="9" y2="21"></line>
                    <line x1="15" y1="3" x2="15" y2="21"></line>
                  </svg>
                </span>
                <h3 class="ps1-card__title">Human-readable Summary — Sample Table</h3>
              </div>
              <div class="ps1-table-wrap">
                <table class="ps1-table">
                  <thead>
                    <tr>
                      <th>Claim ID</th><th>File</th><th>Page</th>
                      <th>Document Classification</th><th>Claim Clinical Rules Checks</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td>CN001</td><td>D1.pdf</td><td>1</td><td>Discharge summary</td><td>Admission date is after surgery date ‖ Clinical rules as per STG are not matching</td></tr>
                    <tr><td>CN001</td><td>D1.pdf</td><td>2</td><td>Clinical note</td><td></td></tr>
                    <tr><td>CN001</td><td>D1.pdf</td><td>3</td><td>Angioplasty report</td><td></td></tr>
                    <tr><td>CN001</td><td>D2.pdf</td><td>1</td><td>X-ray</td><td></td></tr>
                    <tr><td>CN001</td><td>D3.pdf</td><td>1</td><td>CT Scan</td><td></td></tr>
                    <tr><td>CN001</td><td>d4.png</td><td>1</td><td>Patient Photo</td><td></td></tr>
                    <tr><td>CN001</td><td>d4.png</td><td>1</td><td>Angioplasty</td><td>Not required for clinical or claim validation</td></tr>
                  </tbody>
                </table>
              </div>
              <p class="ps1-table-label">Sequence sample output expected:</p>
              <div class="ps1-table-wrap ps1-table-wrap--last">
                <table class="ps1-table">
                  <thead>
                    <tr>
                      <th>Sequence</th><th>Event Type</th><th>Date</th>
                      <th>Source Document</th><th>Temporal Validity</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td>1</td><td>Admission</td><td>02-Feb-26</td><td>Discharge Summary</td><td><span class="ps1-badge ps1-badge--valid">Valid</span></td></tr>
                    <tr><td>2</td><td>Diagnostic Investigation</td><td>02-Feb-26</td><td>CT Scan / X-ray</td><td><span class="ps1-badge ps1-badge--info">Before procedure</span></td></tr>
                    <tr><td>3</td><td>Procedure (Package)</td><td>03-Feb-26</td><td>Angioplasty Report</td><td><span class="ps1-badge ps1-badge--valid">Valid</span></td></tr>
                    <tr><td>4</td><td>Post&#8209;Procedure Monitoring</td><td>04–05 Feb 2026</td><td>Clinical Notes</td><td><span class="ps1-badge ps1-badge--valid">Valid</span></td></tr>
                    <tr><td>5</td><td>Discharge</td><td>06-Feb-26</td><td>Discharge Summary</td><td><span class="ps1-badge ps1-badge--info">After treatment</span></td></tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section class="ps1-card">
              <div class="ps1-card__hd">
                <span class="ps1-card__icon-wrap ps1-card__icon-wrap--gold">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                    stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="8" r="6"></circle>
                    <path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11"></path>
                  </svg>
                </span>
                <h3 class="ps1-card__title">Scoring</h3>
              </div>
              <p class="ps1-scoring-note">Awards will be given to the best 3 teams based on overall score. Minimum qualification score ≥&nbsp;70%.</p>
              <div class="ps1-table-wrap ps1-table-wrap--last">
                <table class="ps1-table">
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th class="ps1-th--center">Weightage</th>
                      <th class="ps1-th--center"><span class="ps1-rank ps1-rank--gold">Rank 1</span></th>
                      <th class="ps1-th--center"><span class="ps1-rank ps1-rank--silver">Rank 2</span></th>
                      <th class="ps1-th--center"><span class="ps1-rank ps1-rank--bronze">Rank 3</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Document Classification</td>
                      <td class="ps1-td--center"><span class="ps1-weight-badge">40%</span></td>
                      <td class="ps1-td--center">F1 ≥ 0.95</td>
                      <td class="ps1-td--center">0.90 ≤ F1 &lt; 0.95</td>
                      <td class="ps1-td--center">0.85 ≤ F1 &lt; 0.90</td>
                    </tr>
                    <tr>
                      <td>Rule creation logic &amp; Provenance detection</td>
                      <td class="ps1-td--center"><span class="ps1-weight-badge">40%</span></td>
                      <td class="ps1-td--center">F1 ≥ 0.96</td>
                      <td class="ps1-td--center">0.90 ≤ F1 &lt; 0.96</td>
                      <td class="ps1-td--center">0.85 ≤ F1 &lt; 0.90</td>
                    </tr>
                    <tr>
                      <td>Design of the solution</td>
                      <td class="ps1-td--center"><span class="ps1-weight-badge">20%</span></td>
                      <td class="ps1-td--center">F1 ≥ 0.93</td>
                      <td class="ps1-td--center">0.88 ≤ F1 &lt; 0.93</td>
                      <td class="ps1-td--center">0.80 ≤ F1 &lt; 0.88</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

          </ng-container>

          <!-- ══════════════════════════════════════════════════
               PS-02
               ══════════════════════════════════════════════════ -->
          <ng-container *ngSwitchCase="2">

            <!-- ① Intro banner -->
            <div class="ps1-intro">
              <div class="ps1-intro__icon-wrap">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"
                  stroke-linecap="round" stroke-linejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
              </div>
              <div class="ps1-intro__body">
                <span class="ps1-intro__eyebrow">Problem Statement Title</span>
                <p class="ps1-intro__text">
                  <strong>Radiological Image&#8209;Based Condition Detection &amp; Report Correlation</strong>
                </p>
              </div>
            </div>

            <!-- ② Objective -->
            <section class="ps1-card">
              <div class="ps1-card__hd">
                <span class="ps1-card__icon-wrap ps1-card__icon-wrap--green">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                    stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <circle cx="12" cy="12" r="6"></circle>
                    <circle cx="12" cy="12" r="2"></circle>
                  </svg>
                </span>
                <h3 class="ps1-card__title">Objective</h3>
              </div>
              <p class="ps1-card__para">To assist AB PMJAY claims adjudication by:</p>
              <ul class="ps1-list ps1-list--check">
                <li>Providing objective, image&#8209;based insights that complement written reports</li>
                <li>Helping reviewers spot inconsistencies between images and reports</li>
              </ul>
            </section>

            <!-- ③ Background / Context -->
            <section class="ps1-card">
              <div class="ps1-card__hd">
                <span class="ps1-card__icon-wrap ps1-card__icon-wrap--blue">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                    stroke-linecap="round" stroke-linejoin="round">
                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                  </svg>
                </span>
                <h3 class="ps1-card__title">Background / Context</h3>
              </div>
              <div class="ps1-card__body">
                <p>In AB PMJAY claims adjudication, several packages require image&#8209;based investigations — such as X&#8209;rays, ultrasounds, CT scans, MRIs etc. — as part of mandatory evidence defined under the Standard Treatment Guidelines (STGs).</p>
                <p>In practice, hospitals usually submit two related forms of evidence for such investigations:</p>
                <ul class="ps1-list">
                  <li>The medical image itself (for example, an X&#8209;ray film or ultrasound film), and</li>
                  <li>A written report or interpretation describing the findings in the image.</li>
                </ul>
                <p>While written reports are essential, they are also easier to modify or misrepresent than the original medical image. For instance:</p>
                <ul class="ps1-list">
                  <li>A written X&#8209;ray report may state that lung infiltrates or fluid are present, suggesting pneumonia.</li>
                  <li>However, the radiological film itself cannot be easily altered and remains the most objective source of evidence.</li>
                </ul>
                <p>Today, adjudicators (PPDs and CPDs) manually:</p>
                <ul class="ps1-list">
                  <li>Open and examine the images much like clinicians do,</li>
                  <li>Compare what they see in the image with what is stated in the written report,</li>
                  <li>Check whether both align with the claimed package, stage of care, and episode timeline.</li>
                </ul>
                <p>This manual image reading and correlation requires specialist expertise, is time&#8209;consuming, and becomes challenging when many images are submitted or image quality varies.</p>
              </div>
            </section>

            <!-- ④ Problem Statement -->
            <section class="ps1-card">
              <div class="ps1-card__hd">
                <span class="ps1-card__icon-wrap ps1-card__icon-wrap--amber">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                    stroke-linecap="round" stroke-linejoin="round">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                    <line x1="12" y1="9" x2="12" y2="13"></line>
                    <line x1="12" y1="17" x2="12.01" y2="17"></line>
                  </svg>
                </span>
                <h3 class="ps1-card__title">Problem Statement</h3>
              </div>
              <p class="ps1-card__para">Design an assistive solution that can read and interpret medical images to help adjudicators:</p>
              <ul class="ps1-list ps1-list--check">
                <li>Understand what the image itself appears to show</li>
                <li>Correlate the image findings with the written report submitted by the hospital</li>
                <li>Verify whether both are consistent with the claimed condition, stage, and episode timeline, as expected under Standard Treatment Guidelines (STGs)</li>
              </ul>
            </section>

            <!-- ⑤ Expected Solution -->
            <section class="ps1-card">
              <div class="ps1-card__hd">
                <span class="ps1-card__icon-wrap ps1-card__icon-wrap--green">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                    stroke-linecap="round" stroke-linejoin="round">
                    <line x1="9" y1="18" x2="15" y2="18"></line>
                    <line x1="10" y1="22" x2="14" y2="22"></line>
                    <path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14"></path>
                  </svg>
                </span>
                <h3 class="ps1-card__title">Expected Solution</h3>
              </div>
              <div class="ps1-card__body">

                <div class="ps1-subsection">
                  <p class="ps1-subsection__title">1. Read and Describe Medical Images for Condition&#8209;Relevant Features</p>
                  <p>The solution should:</p>
                  <ul class="ps1-list">
                    <li>Analyze image&#8209;based investigations: X&#8209;rays (chest X&#8209;ray, angiogram, urethrogram etc.), ultrasound, CT, MRI</li>
                    <li>Detect and describe key visual features relevant to the claimed condition or procedure, for example:
                      <ul class="ps1-sublist">
                        <li>Lung opacities or fluid patterns on chest X&#8209;ray</li>
                        <li>Gallstones on ultrasound</li>
                      </ul>
                    </li>
                    <li>Express findings as observations, such as:
                      <ul class="ps1-sublist">
                        <li>"Features consistent with …"</li>
                        <li>"Features not clearly seen …"</li>
                      </ul>
                    </li>
                  </ul>
                </div>

                <div class="ps1-subsection">
                  <p class="ps1-subsection__title">2. Correlate Image Findings with Written Reports and Claims Context</p>
                  <p>The solution should:</p>
                  <ul class="ps1-list">
                    <li>First validate the internal consistency of the written report, i.e., whether the observations align with the interpretation in the second part of the report</li>
                    <li>Compare what the image appears to show with what is stated in the written report submitted by the hospital</li>
                    <li>Highlight:
                      <ul class="ps1-sublist">
                        <li>Areas where the image and report appear consistent, and</li>
                        <li>Areas where they appear inconsistent or unsupported</li>
                      </ul>
                    </li>
                    <li>Present this as a review note</li>
                  </ul>
                </div>

                <div class="ps1-subsection">
                  <p class="ps1-subsection__title">3. Stage and Timeline&#8209;Aware Flagging</p>
                  <p>The solution should:</p>
                  <ul class="ps1-list">
                    <li>Identify whether the image:
                      <ul class="ps1-sublist">
                        <li>Was taken before treatment (relevant for pre&#8209;authorization), or</li>
                        <li>After treatment (relevant for claim submission)</li>
                      </ul>
                    </li>
                    <li>Check whether image timing aligns with the episode timeline</li>
                    <li>Flag images that appear:
                      <ul class="ps1-sublist">
                        <li>Too old,</li>
                        <li>Unrelated to the current admission, or</li>
                        <li>Possibly from a different care episode</li>
                      </ul>
                    </li>
                  </ul>
                </div>

              </div>
            </section>

            <!-- ⑥ Expected Output -->
            <section class="ps1-card">
              <div class="ps1-card__hd">
                <span class="ps1-card__icon-wrap ps1-card__icon-wrap--blue">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                    stroke-linecap="round" stroke-linejoin="round">
                    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
                    <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
                  </svg>
                </span>
                <h3 class="ps1-card__title">Expected Output</h3>
              </div>
              <p class="ps1-card__para">An Image Interpretation &amp; Correlation Assistant that:</p>
              <ul class="ps1-list ps1-list--check">
                <li>Reads and analyzes submitted medical images</li>
                <li>Summarizes what the image appears to show in clear, plain language</li>
                <li>Correlates image findings with the written report provided</li>
                <li>Flags possible mismatches or inconsistencies for reviewer attention</li>
                <li>Presents findings in a way that is easy for PPDs and CPDs to review</li>
              </ul>
              <div class="ps1-important-note">
                <span class="ps1-important-note__label">Important</span>
                <p>The solution is strictly assistive. It does not diagnose, decide medical necessity, approve packages, or determine payments.</p>
              </div>
            </section>

            <!-- ⑦ Note for Participants -->
            <section class="ps1-card">
              <div class="ps1-card__hd">
                <span class="ps1-card__icon-wrap ps1-card__icon-wrap--amber">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                    stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                </span>
                <h3 class="ps1-card__title">Note for Participants</h3>
              </div>
              <p class="ps1-card__para">Participants should demonstrate:</p>
              <ul class="ps1-list ps1-list--check">
                <li>Actual image reading and interpretation</li>
                <li>Clear explanation of visual observations</li>
                <li>Correlation between images and written reports</li>
                <li>Honest handling of uncertainty and unclear images</li>
              </ul>
            </section>

            <!-- ⑧ Scoring -->
            <section class="ps1-card">
              <div class="ps1-card__hd">
                <span class="ps1-card__icon-wrap ps1-card__icon-wrap--gold">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                    stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="8" r="6"></circle>
                    <path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11"></path>
                  </svg>
                </span>
                <h3 class="ps1-card__title">Scoring</h3>
              </div>
              <p class="ps1-scoring-note">Minimum qualification score ≥&nbsp;70%. Awards will be given to the best 3 teams based on overall score.</p>

              <p class="ps1-table-label">Detailed Scoring Scheme</p>
              <div class="ps1-table-wrap">
                <table class="ps1-table">
                  <thead>
                    <tr>
                      <th style="min-width:200px">Category</th>
                      <th class="ps1-th--center" style="min-width:90px">Weightage</th>
                      <th style="min-width:220px"><span class="ps1-rank ps1-rank--gold">Rank 1</span></th>
                      <th style="min-width:220px"><span class="ps1-rank ps1-rank--silver">Rank 2</span></th>
                      <th style="min-width:220px"><span class="ps1-rank ps1-rank--bronze">Rank 3</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Medical Image Feature Detection &amp; Description</td>
                      <td class="ps1-td--center"><span class="ps1-weight-badge">30%</span></td>
                      <td>Accurately detects and clearly describes all key condition&#8209;relevant visual features across modalities. Uses appropriate radiology&#8209;style observations and explicitly acknowledges uncertainty where applicable.</td>
                      <td>Detects most major features, but descriptions may lack depth, precision, or modality&#8209;specific language. Minor findings or nuance may be missed.</td>
                      <td>Detects only obvious or generic features. Limited specificity, superficial descriptions, or occasional misinterpretation of image content.</td>
                    </tr>
                    <tr>
                      <td>Handling of Image Quality &amp; Uncertainty</td>
                      <td class="ps1-td--center"><span class="ps1-weight-badge">10%</span></td>
                      <td>Robustly handles variable image quality (poor exposure, partial views, artifacts). Clearly states limitations and differentiates "not seen" versus "not assessable."</td>
                      <td>Acknowledges poor image quality but handling is inconsistent or not clearly tied to conclusions.</td>
                      <td>Little or no acknowledgment of image quality issues; assumes findings despite unclear images.</td>
                    </tr>
                    <tr>
                      <td>Internal Consistency Check of Written Reports</td>
                      <td class="ps1-td--center"><span class="ps1-weight-badge">15%</span></td>
                      <td>Reliably assesses whether report observations align with report impressions and flags internal contradictions (e.g., normal findings but abnormal conclusion).</td>
                      <td>Identifies major inconsistencies but misses subtle internal mismatches.</td>
                      <td>Minimal or no validation of internal report consistency.</td>
                    </tr>
                    <tr>
                      <td>Image&#8209;to&#8209;Report Correlation</td>
                      <td class="ps1-td--center"><span class="ps1-weight-badge">20%</span></td>
                      <td>Clearly compares image findings with report statements and classifies them as consistent, partially supported, or unsupported, with concise justification.</td>
                      <td>Performs correlation but explanations may be vague or overly binary (match / no match).</td>
                      <td>Correlation is superficial or largely restates the report without referencing image evidence.</td>
                    </tr>
                    <tr>
                      <td>Claim, Package &amp; STG Alignment</td>
                      <td class="ps1-td--center"><span class="ps1-weight-badge">10%</span></td>
                      <td>Correctly contextualizes findings against the claimed package and expected STG&#8209;driven imaging intent, without making diagnostic or approval decisions.</td>
                      <td>General alignment to package, but STG relevance or procedural context is weak.</td>
                      <td>Little understanding of package&#8209;specific imaging expectations.</td>
                    </tr>
                    <tr>
                      <td>Timeline &amp; Stage&#8209;of&#8209;Care Awareness</td>
                      <td class="ps1-td--center"><span class="ps1-weight-badge">10%</span></td>
                      <td>Accurately identifies whether images are pre&#8209;procedure, intra&#8209;procedure, or post&#8209;procedure, and flags mismatched timing or possible reuse from previous episodes.</td>
                      <td>Timeline awareness present but inconsistent or partially inferred without clarity.</td>
                      <td>Timeline largely ignored or incorrectly inferred.</td>
                    </tr>
                    <tr>
                      <td>Reviewer&#8209;Friendly Output &amp; Explainability</td>
                      <td class="ps1-td--center"><span class="ps1-weight-badge">5%</span></td>
                      <td>Outputs are clear, concise, structured, and suited for PPD/CPD review. Flags are easy to understand and actionable.</td>
                      <td>Output understandable but verbose, unstructured, or harder for quick review.</td>
                      <td>Output is confusing, overly technical, or not reviewer&#8209;friendly.</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p class="ps1-table-label">Rank Cut&#8209;Offs (Overall Score)</p>
              <div class="ps1-table-wrap ps1-table-wrap--last">
                <table class="ps1-table">
                  <thead>
                    <tr>
                      <th style="min-width:100px">Rank</th>
                      <th style="min-width:120px">Score Range</th>
                      <th style="min-width:320px">Interpretation</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><span class="ps1-rank ps1-rank--gold">Rank 1</span></td>
                      <td><strong>≥ 85 / 100</strong></td>
                      <td>Production&#8209;ready assistive solution demonstrating strong image reading, correlation, and adjudication relevance across all four packages.</td>
                    </tr>
                    <tr>
                      <td><span class="ps1-rank ps1-rank--silver">Rank 2</span></td>
                      <td><strong>70 – 84 / 100</strong></td>
                      <td>Solid solution with clear value, but gaps in depth, consistency, or explainability.</td>
                    </tr>
                    <tr>
                      <td><span class="ps1-rank ps1-rank--bronze">Rank 3</span></td>
                      <td><strong>55 – 69 / 100</strong></td>
                      <td>Basic capability demonstrated, but limited robustness, accuracy, or adjudication usefulness.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

          </ng-container>

          <!-- ══════════════════════════════════════════════════
               PS-03
               ══════════════════════════════════════════════════ -->
          <ng-container *ngSwitchCase="3">

            <!-- ① Intro banner -->
            <div class="ps1-intro">
              <div class="ps1-intro__icon-wrap">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"
                  stroke-linecap="round" stroke-linejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                </svg>
              </div>
              <div class="ps1-intro__body">
                <span class="ps1-intro__eyebrow">Problem Statement Title</span>
                <p class="ps1-intro__text">
                  <strong>Document Forgery / Deepfake Detection</strong>
                </p>
              </div>
            </div>

            <!-- ② Objective -->
            <section class="ps1-card">
              <div class="ps1-card__hd">
                <span class="ps1-card__icon-wrap ps1-card__icon-wrap--green">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                    stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <circle cx="12" cy="12" r="6"></circle>
                    <circle cx="12" cy="12" r="2"></circle>
                  </svg>
                </span>
                <h3 class="ps1-card__title">Objective</h3>
              </div>
              <div class="ps1-card__body">
                <p>Build a solution to detect and highlight specific areas in a document where tampering has been done. The focus is on identifying small, localized changes rather than classifying the whole document.</p>
              </div>
            </section>

            <!-- ③ Scope of Tampering -->
            <section class="ps1-card">
              <div class="ps1-card__hd">
                <span class="ps1-card__icon-wrap ps1-card__icon-wrap--amber">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                    stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  </svg>
                </span>
                <h3 class="ps1-card__title">Scope of Tampering</h3>
              </div>
              <p class="ps1-card__para">The solution should be able to detect all of the following within a document:</p>
              <ul class="ps1-list">
                <li>Copy&#8209;paste of content within the document</li>
                <li>Overwriting text on existing text</li>
                <li>Adding new content (text, stamp, signature, etc.)</li>
                <li>Removing or erasing part of text or image</li>
                <li>Merging content from different documents (e.g., header from one, body from another)</li>
                <li>Watermark removal</li>
                <li>Irregular spacing between words or characters</li>
                <li>Fully AI&#8209;generated documents (e.g., fake diagnostic reports, scanned reports)</li>
                <li>Partial edits using AI (e.g., name, amount, or date changed)</li>
              </ul>
            </section>

            <!-- ④ Functional Expectations -->
            <section class="ps1-card">
              <div class="ps1-card__hd">
                <span class="ps1-card__icon-wrap ps1-card__icon-wrap--blue">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                    stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="9 11 12 14 22 4"></polyline>
                    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
                  </svg>
                </span>
                <h3 class="ps1-card__title">Functional Expectations from the Solution</h3>
              </div>
              <ul class="ps1-list ps1-list--check">
                <li>Accept input as image or PDF</li>
                <li>Detect all tampered regions in the document</li>
                <li>Provide output as:
                  <ul class="ps1-sublist">
                    <li>Bounding boxes around suspicious areas</li>
                    <li>Type of tampering for each region</li>
                  </ul>
                </li>
                <li>Handle:
                  <ul class="ps1-sublist">
                    <li>Multiple tampered regions in a single document</li>
                    <li>Scanned and low&#8209;quality documents</li>
                  </ul>
                </li>
                <li>Should not rely on manual intervention</li>
              </ul>
              <div class="ps1-important-note">
                <span class="ps1-important-note__label">Constraint</span>
                <p>Use of LLMs or generative AI models is <strong>not allowed</strong> for this problem statement.</p>
              </div>
            </section>

            <!-- ⑤ Evaluation Metric / Scoring -->
            <section class="ps1-card">
              <div class="ps1-card__hd">
                <span class="ps1-card__icon-wrap ps1-card__icon-wrap--gold">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                    stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="8" r="6"></circle>
                    <path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11"></path>
                  </svg>
                </span>
                <h3 class="ps1-card__title">Evaluation Metric</h3>
              </div>
              <p class="ps1-scoring-note">Awards will be given to the best 3 teams based on overall score. Minimum qualification score ≥&nbsp;70%.</p>
              <div class="ps1-table-wrap ps1-table-wrap--last">
                <table class="ps1-table">
                  <thead>
                    <tr>
                      <th style="min-width:36px">#</th>
                      <th style="min-width:200px">Criteria</th>
                      <th class="ps1-th--center" style="min-width:90px">Weightage</th>
                      <th class="ps1-th--center" style="min-width:140px"><span class="ps1-rank ps1-rank--gold">Rank 1</span></th>
                      <th class="ps1-th--center" style="min-width:140px"><span class="ps1-rank ps1-rank--silver">Rank 2</span></th>
                      <th class="ps1-th--center" style="min-width:140px"><span class="ps1-rank ps1-rank--bronze">Rank 3</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td class="ps1-td--center">1</td>
                      <td>Copy&#8209;paste of content within the document</td>
                      <td class="ps1-td--center"><span class="ps1-weight-badge">10%</span></td>
                      <td class="ps1-td--center">F1 ≥ 0.95</td>
                      <td class="ps1-td--center">0.90 ≤ F1 &lt; 0.95</td>
                      <td class="ps1-td--center">0.85 ≤ F1 &lt; 0.90</td>
                    </tr>
                    <tr>
                      <td class="ps1-td--center">2</td>
                      <td>Overwriting text on existing text</td>
                      <td class="ps1-td--center"><span class="ps1-weight-badge">10%</span></td>
                      <td class="ps1-td--center">F1 ≥ 0.94</td>
                      <td class="ps1-td--center">0.89 ≤ F1 &lt; 0.94</td>
                      <td class="ps1-td--center">0.84 ≤ F1 &lt; 0.89</td>
                    </tr>
                    <tr>
                      <td class="ps1-td--center">3</td>
                      <td>Adding new content (text, stamp, signature, etc.)</td>
                      <td class="ps1-td--center"><span class="ps1-weight-badge">15%</span></td>
                      <td class="ps1-td--center">F1 ≥ 0.96</td>
                      <td class="ps1-td--center">0.91 ≤ F1 &lt; 0.96</td>
                      <td class="ps1-td--center">0.86 ≤ F1 &lt; 0.91</td>
                    </tr>
                    <tr>
                      <td class="ps1-td--center">4</td>
                      <td>Removing or erasing part of text or image</td>
                      <td class="ps1-td--center"><span class="ps1-weight-badge">10%</span></td>
                      <td class="ps1-td--center">F1 ≥ 0.93</td>
                      <td class="ps1-td--center">0.88 ≤ F1 &lt; 0.93</td>
                      <td class="ps1-td--center">0.83 ≤ F1 &lt; 0.88</td>
                    </tr>
                    <tr>
                      <td class="ps1-td--center">5</td>
                      <td>Merging content from different documents</td>
                      <td class="ps1-td--center"><span class="ps1-weight-badge">10%</span></td>
                      <td class="ps1-td--center">F1 ≥ 0.94</td>
                      <td class="ps1-td--center">0.89 ≤ F1 &lt; 0.94</td>
                      <td class="ps1-td--center">0.84 ≤ F1 &lt; 0.89</td>
                    </tr>
                    <tr>
                      <td class="ps1-td--center">6</td>
                      <td>Watermark removal</td>
                      <td class="ps1-td--center"><span class="ps1-weight-badge">5%</span></td>
                      <td class="ps1-td--center">F1 ≥ 0.92</td>
                      <td class="ps1-td--center">0.87 ≤ F1 &lt; 0.92</td>
                      <td class="ps1-td--center">0.82 ≤ F1 &lt; 0.87</td>
                    </tr>
                    <tr>
                      <td class="ps1-td--center">7</td>
                      <td>Irregular spacing between words or characters</td>
                      <td class="ps1-td--center"><span class="ps1-weight-badge">10%</span></td>
                      <td class="ps1-td--center">F1 ≥ 0.93</td>
                      <td class="ps1-td--center">0.88 ≤ F1 &lt; 0.93</td>
                      <td class="ps1-td--center">0.83 ≤ F1 &lt; 0.88</td>
                    </tr>
                    <tr>
                      <td class="ps1-td--center">8</td>
                      <td>Fully AI&#8209;generated documents</td>
                      <td class="ps1-td--center"><span class="ps1-weight-badge">15%</span></td>
                      <td class="ps1-td--center">F1 ≥ 0.96</td>
                      <td class="ps1-td--center">0.91 ≤ F1 &lt; 0.96</td>
                      <td class="ps1-td--center">0.86 ≤ F1 &lt; 0.91</td>
                    </tr>
                    <tr>
                      <td class="ps1-td--center">9</td>
                      <td>Partial edits using AI (name, date, amount, etc.)</td>
                      <td class="ps1-td--center"><span class="ps1-weight-badge">15%</span></td>
                      <td class="ps1-td--center">F1 ≥ 0.95</td>
                      <td class="ps1-td--center">0.90 ≤ F1 &lt; 0.95</td>
                      <td class="ps1-td--center">0.85 ≤ F1 &lt; 0.90</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

          </ng-container>

          <!-- ══════════════════════════════════════════════════
               Generic (PS-04 and beyond) — overview + NHA notice
               ══════════════════════════════════════════════════ -->
          <ng-container *ngSwitchDefault>
            <section class="ps-detail__section">
              <h2 class="ps-detail__section-title">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                  stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                Overview
              </h2>
              <p class="ps-detail__desc">{{ ps!.description }}</p>
            </section>
            <div class="ps-detail__nha-notice">
              <div class="ps-detail__nha-notice-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                  stroke-linecap="round" stroke-linejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                  <polyline points="10 9 9 9 8 9"></polyline>
                </svg>
              </div>
              <div class="ps-detail__nha-notice-body">
                <strong>Full problem statement available on NHA</strong>
                <p>The detailed problem statement including datasets, evaluation criteria, submission guidelines,
                  and technical specifications is published on the official NHA website.</p>
                <button type="button" class="ps-detail__cta" (click)="openOnNha()">
                  View Full Problem Statement on NHA
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
                    stroke-linecap="round" stroke-linejoin="round">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                    <polyline points="15 3 21 3 21 9"></polyline>
                    <line x1="10" y1="14" x2="21" y2="3"></line>
                  </svg>
                </button>
              </div>
            </div>
          </ng-container>

        </div>

        <!-- ── Sidebar ──────────────────────────────────────── -->
        <aside class="ps-detail__sidebar">
          <div class="ps-detail__prize-card">
            <div class="ps-detail__prize-card-header">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="8" r="6"></circle>
                <path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11"></path>
              </svg>
              Prize Pool
            </div>
            <div class="ps-detail__prize-rows">
              <div class="ps-detail__prize-row"
                *ngFor="let prize of ps!.prizes; let i = index"
                [class.prize--gold]="i === 0"
                [class.prize--silver]="i === 1"
                [class.prize--bronze]="i === 2">
                <span class="prize__rank">{{ prize.rank }}</span>
                <span class="prize__amount">{{ prize.amount }}</span>
              </div>
            </div>
          </div>

          <div class="ps-detail__nav-card">
            <p class="ps-detail__nav-label">Other Problem Statements</p>
            <a
              class="ps-detail__nav-link"
              [class.ps-detail__nav-link--active]="ps?.id === 1"
              [attr.aria-current]="ps?.id === 1 ? 'page' : null"
              routerLink="/reference/problem-statement/1"
            >
              <span>PS-01</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
                stroke-linecap="round" stroke-linejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline>
              </svg>
            </a>
            <a
              class="ps-detail__nav-link"
              [class.ps-detail__nav-link--active]="ps?.id === 2"
              [attr.aria-current]="ps?.id === 2 ? 'page' : null"
              routerLink="/reference/problem-statement/2"
            >
              <span>PS-02</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
                stroke-linecap="round" stroke-linejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline>
              </svg>
            </a>
            <a
              class="ps-detail__nav-link"
              [class.ps-detail__nav-link--active]="ps?.id === 3"
              [attr.aria-current]="ps?.id === 3 ? 'page' : null"
              routerLink="/reference/problem-statement/3"
            >
              <span>PS-03</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
                stroke-linecap="round" stroke-linejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline>
              </svg>
            </a>
            <a class="ps-detail__nav-link ps-detail__nav-link--all" routerLink="/reference">
              ← All Problem Statements
            </a>
          </div>

          <div class="ps-detail__submission-closed" role="note">
            <p class="ps-detail__submission-closed-text">
              The hackathon submission window has concluded.
            </p>
            <p class="ps-detail__submission-closed-text">
              The final deadline for submissions was April 1 at 11:59 PM.
            </p>
          </div>
          <button
            *ngIf="outputGuidelinesFileUrl"
            type="button"
            class="ps-detail__sandbox-btn ps-detail__sandbox-btn--sidebar"
            (click)="downloadOutputGuidelines()"
            aria-label="View guidelines"
          >
            View guidelines
          </button>
          <div class="ps-detail__resources-card" *ngIf="problemStatementResources.length > 0">
            <p class="ps-detail__resources-title">Resources</p>
            <button
              type="button"
              class="ps-detail__resource-btn"
              *ngFor="let resource of problemStatementResources"
              (click)="downloadProblemStatementResource(resource)"
              [attr.aria-label]="'Download ' + resource.label"
            >
              <span class="ps-detail__resource-meta">
                <strong>{{ resource.label }}</strong>
                <small>{{ resource.description }}</small>
              </span>
              <span class="ps-detail__resource-action">Download</span>
            </button>
          </div>
        </aside>
      </div>
    </div>

    <app-footer></app-footer>
    <app-login-modal *ngIf="showLoginModal" (visibleChange)="showLoginModal = false"></app-login-modal>
    <app-modal
      [(visible)]="showEvaluateModal"
      [header]="''"
      [width]="'520px'"
      [modal]="true"
      [closable]="true"
      [showFooter]="false"
    >
      <div class="ps-detail__evaluate-modal">
        <div class="ps-detail__evaluate-loader" aria-hidden="true"></div>
        <p>Your evaluation of the notebook is in place.<br>Please wait for a while !!</p>
      </div>
    </app-modal>
    <app-modal
      [(visible)]="showNotebookRunningModal"
      [header]="''"
      [width]="'min(92vw, 560px)'"
      [modal]="true"
      [closable]="!isNotebookListNavigating"
      [showFooter]="false"
    >
      <div class="ps-detail__nb-running-modal">
        <div class="ps-detail__nb-running-modal__head">
          <div class="ps-detail__nb-running-modal__icon" aria-hidden="true">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75"
              stroke-linecap="round" stroke-linejoin="round">
              <rect x="2" y="3" width="20" height="14" rx="2"></rect>
              <line x1="8" y1="21" x2="16" y2="21"></line>
              <line x1="12" y1="17" x2="12" y2="21"></line>
              <path d="M10 9h4M10 12h2" stroke-width="2" stroke-linecap="round"></path>
            </svg>
          </div>
          <h3 class="ps-detail__nb-running-modal__title">Stop Evaluating</h3>
        </div>
        <div class="ps-detail__nb-running-modal__chip">
          <span class="ps-detail__nb-running-modal__chip-label">Running notebook</span>
          <code class="ps-detail__nb-running-modal__chip-value">{{ runningNotebookName }}</code>
        </div>
        <ol class="ps-detail__nb-running-modal__steps">
          <li>Go to your notebook list.</li>
          <li>Use <strong>Stop</strong> on the running notebook.</li>
          <li>Come back to this page and click <strong>Evaluate</strong>.</li>
        </ol>
        <div class="ps-detail__nb-running-modal__actions">
          <button
            type="button"
            class="ps-detail__nb-running-modal__btn ps-detail__nb-running-modal__btn--secondary"
            [disabled]="isNotebookListNavigating"
            (click)="closeNotebookRunningModal()"
          >
            Not now
          </button>
          <button
            type="button"
            class="ps-detail__nb-running-modal__btn ps-detail__nb-running-modal__btn--primary"
            [disabled]="isNotebookListNavigating"
            (click)="goToMyProjectsNotebookListFromRunningModal()"
          >
            {{ isNotebookListNavigating ? 'Opening…' : 'Go to notebook list' }}
          </button>
        </div>
      </div>
    </app-modal>
    <app-modal
      [(visible)]="showNoEvaluationModal"
      [header]="''"
      [width]="'520px'"
      [modal]="true"
      [closable]="true"
      [showFooter]="false"
    >
      <div class="ps-detail__evaluate-modal ps-detail__evaluate-modal--empty">
        <p class="ps-detail__no-eval-title">No evaluation found</p>
        <p class="ps-detail__no-eval-desc">There are no evaluation workflows for this problem statement yet.</p>
      </div>
    </app-modal>
    <app-modal
      [(visible)]="showMyEvaluationsModal"
      [header]="''"
      [width]="'min(92vw, 960px)'"
      [modal]="true"
      [closable]="true"
      [showFooter]="false"
    >
      <div class="hack-my-eval">
        <h3 class="hack-my-eval__title">My evaluations</h3>
        <p class="hack-my-eval__subtitle">Your evaluation workflow runs for this problem statement.</p>
        <div class="hack-my-eval__table-scroll">
          <table class="hack-my-eval__table">
            <thead>
              <tr>
                <th>Workflow</th>
                <th>Status</th>
                <th>Score</th>
                <th>Tests</th>
                <th>Created</th>
                <th>Finished</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              <ng-container *ngFor="let row of myWorkflowRows">
                <tr>
                  <td class="hack-my-eval__mono">
                    <div>{{ row.workflow_name }}</div>
                    <button
                      type="button"
                      class="hack-my-eval__log-toggle"
                      [disabled]="!row.id || isWorkflowLogLoading(row)"
                      (click)="toggleWorkflowLog(row)"
                    >
                      {{ getWorkflowToggleLabel(row) }}
                    </button>
                  </td>
                  <td><span [ngClass]="workflowStatusClass(row.status)">{{ row.status }}</span></td>
                  <td>{{ formatWorkflowScore(row.score) }}</td>
                  <td>
                    <ng-container *ngIf="row.total_tests != null; else dashTests">
                      {{ row.passed_tests ?? 0 }}/{{ row.failed_tests ?? 0 }}/{{ row.total_tests }}
                    </ng-container>
                    <ng-template #dashTests>—</ng-template>
                  </td>
                  <td class="hack-my-eval__date">{{ formatWorkflowDate(row.created_at) }}</td>
                  <td class="hack-my-eval__date">{{ formatWorkflowDate(row.finished_at) }}</td>
                  <td class="hack-my-eval__notes">{{ row.error_message || '—' }}</td>
                </tr>
                <tr *ngIf="expandedWorkflowId === row.id" class="hack-my-eval__log-row">
                  <td colspan="7">
                    <div class="hack-my-eval__log-label">Output</div>
                    <pre class="hack-my-eval__log-content">{{ getWorkflowLogText(row) }}</pre>
                  </td>
                </tr>
              </ng-container>
            </tbody>
          </table>
        </div>
      </div>
    </app-modal>
  `,
  styleUrl: './problem-statement-detail.component.scss',
})
export class ProblemStatementDetailComponent implements OnInit, OnDestroy {
  private readonly evaluateCooldownStoragePrefix = 'nha-hackathon-evaluate-cooldown:';
  private cooldownTicker: ReturnType<typeof setInterval> | null = null;
  private nowMs = Date.now();
  ps: ProblemStatement | null = null;
  isLoading = true;
  notFound = false;
  showLoginModal = false;
  showEvaluateModal = false;
  showNotebookRunningModal = false;
  showNoEvaluationModal = false;
  showMyEvaluationsModal = false;
  runningNotebookName = '';
  isNotebookListNavigating = false;
  myWorkflowRows: HackathonMyWorkflowRow[] = [];
  expandedWorkflowId: string | null = null;
  workflowLogsById: Record<string, string> = {};
  workflowLogsLoadingById: Record<string, boolean> = {};
  isEvaluateSubmitting = false;
  isViewEvaluationLoading = false;
  /** Set to false only after a successful notebook list fetch found no PS-linked notebook (logged-in users). */
  private hasNotebookForCurrentProblemStatement: boolean | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private contentService: NhaContentService,
    private keycloak: KeycloakService,
    private rolesService: RolesApiService,
    private sandboxApiService: SandboxApiService,
    private leadershipApiService: LeadershipApiService,
    private toasterService: ToasterService,
  ) {}

  ngOnInit(): void {
    this.startCooldownTicker();
    this.route.paramMap.subscribe((params) => {
      const id = Number(params.get('id'));
      this.isLoading = true;
      this.notFound = false;
      this.hasNotebookForCurrentProblemStatement = null;

      this.contentService.fetchHackathonContent().subscribe({
        next: ({ problemStatements }) => {
          const list = problemStatements.length > 0 ? problemStatements : STATIC_PROBLEM_STATEMENTS;
          this.ps = list.find((p) => p.id === id) ?? null;
          if (!this.ps) this.notFound = true;
          this.isLoading = false;
          void this.refreshNotebookEligibilityForEvaluate();
        },
        error: () => {
          this.ps = STATIC_PROBLEM_STATEMENTS.find((p) => p.id === id) ?? null;
          if (!this.ps) this.notFound = true;
          this.isLoading = false;
          void this.refreshNotebookEligibilityForEvaluate();
        },
      });
    });
  }

  @HostListener('document:visibilitychange')
  onDocumentVisibilityChange(): void {
    if (document.visibilityState === 'visible' && this.ps?.id) {
      void this.refreshNotebookEligibilityForEvaluate();
    }
  }

  ngOnDestroy(): void {
    this.stopCooldownTicker();
  }

  goBack(): void {
    this.router.navigate(['/reference']);
  }

  openOnNha(): void {
    const url = this.ps?.externalUrl ?? `https://nha.gov.in/problemStatement${this.ps?.id}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  async openSandbox(): Promise<void> {
    await this.handleSandboxAction('sandbox');
  }

  async openEvaluate(): Promise<void> {
    try {
      const isLoggedIn = await this.keycloak.isLoggedIn();
      if (!isLoggedIn) {
        this.showLoginModal = true;
        return;
      }
      if (!this.ps?.id || this.isEvaluateSubmitting) {
        return;
      }
      if (this.isEvaluateDisabledNoPsNotebook()) {
        this.toasterService.warning('Create or open a sandbox notebook for this problem statement before evaluating.');
        return;
      }

      const payload = this.getEvaluationPayloadForProblemStatement(this.ps.id);
      if (!payload) {
        this.toasterService.warning('Evaluation is not configured for this problem statement.');
        return;
      }

      const canContinue = await this.ensureNotebookNotRunningForEvaluate();
      if (!canContinue) {
        return;
      }

      this.isEvaluateSubmitting = true;
      const accessToken = await this.keycloak.getToken();
      const submitResponse: HackathonSubmitResponse =
        await this.leadershipApiService.submitHackathonEvaluation(payload.challengeName, payload.pvc, accessToken);
      if (submitResponse?._httpStatus === 202) {
        this.showEvaluateModal = true;
      } else {
        this.toasterService.success('Evaluation submitted successfully.');
      }
    } catch (error) {
      console.error('Error during evaluate action:', error);
      const typedError = error as HackathonSubmitError;
      this.toasterService.error(this.getApiErrorMessage(typedError, 'Unable to submit evaluation. Please try again.'));
    } finally {
      this.isEvaluateSubmitting = false;
    }
  }

  async viewMyEvaluation(): Promise<void> {
    if (!this.ps?.id || this.isViewEvaluationLoading) {
      return;
    }

    const isLoggedIn = await this.keycloak.isLoggedIn();
    if (!isLoggedIn) {
      this.showLoginModal = true;
      return;
    }

    const payload = this.getEvaluationPayloadForProblemStatement(this.ps.id);
    if (!payload) {
      this.toasterService.warning('Evaluation is not configured for this problem statement.');
      return;
    }

    this.isViewEvaluationLoading = true;
    try {
      const accessToken = await this.keycloak.getToken();
      const workflowsResponse = await this.leadershipApiService.getHackathonMyWorkflows(payload.pvc, accessToken);
      const workflows = extractHackathonMyWorkflowsListFromResponse(workflowsResponse);
      this.myWorkflowRows = workflows;
      if (workflows.length > 0) {
        this.showMyEvaluationsModal = true;
      } else {
        this.showNoEvaluationModal = true;
      }
    } catch (error) {
      console.error('Error fetching my evaluation workflows:', error);
      const typedError = error as HackathonSubmitError;
      this.toasterService.error(this.getApiErrorMessage(typedError, 'Unable to fetch your evaluations. Please try again.'));
    } finally {
      this.isViewEvaluationLoading = false;
    }
  }

  formatWorkflowScore(value: number | undefined): string {
    if (value === undefined || value === null || Number.isNaN(Number(value))) {
      return '—';
    }
    return (Math.round(Number(value) * 10000) / 10000).toFixed(4);
  }

  formatWorkflowDate(iso: string | undefined): string {
    if (!iso) {
      return '—';
    }
    const parsed = Date.parse(iso);
    if (Number.isNaN(parsed)) {
      return iso;
    }
    return new Date(parsed).toLocaleString();
  }

  workflowStatusClass(status: string | undefined): string {
    const key = (status || '').toLowerCase();
    if (key === 'succeeded') {
      return 'hack-my-eval__status hack-my-eval__status--ok';
    }
    if (key === 'failed') {
      return 'hack-my-eval__status hack-my-eval__status--fail';
    }
    if (key === 'running') {
      return 'hack-my-eval__status hack-my-eval__status--run';
    }
    return 'hack-my-eval__status';
  }

  async toggleWorkflowLog(row: HackathonMyWorkflowRow): Promise<void> {
    const runId = row.id || '';
    if (!runId) {
      return;
    }

    if (this.expandedWorkflowId === runId) {
      this.expandedWorkflowId = null;
      return;
    }

    this.expandedWorkflowId = runId;
    if (this.workflowLogsById[runId] || this.workflowLogsLoadingById[runId]) {
      return;
    }

    this.workflowLogsLoadingById[runId] = true;
    try {
      const accessToken = await this.keycloak.getToken();
      const runResponse: HackathonRunDetailsResponse =
        await this.leadershipApiService.getHackathonRunDetails(runId, accessToken);
      this.workflowLogsById[runId] = this.normalizeRunOutput(runResponse['output']);
    } catch (error) {
      console.error('Error fetching workflow logs:', error);
      const typedError = error as HackathonSubmitError;
      this.workflowLogsById[runId] = this.getApiErrorMessage(typedError, 'Unable to fetch logs.');
    } finally {
      this.workflowLogsLoadingById[runId] = false;
    }
  }

  getWorkflowToggleLabel(row: HackathonMyWorkflowRow): string {
    const runId = row.id || '';
    if (!runId) {
      return 'No logs';
    }
    if (this.workflowLogsLoadingById[runId]) {
      return 'Loading logs...';
    }
    return this.expandedWorkflowId === runId ? 'Hide logs' : 'View logs';
  }

  getWorkflowLogText(row: HackathonMyWorkflowRow): string {
    const runId = row.id || '';
    if (!runId) {
      return 'Run id is unavailable for this row.';
    }
    if (this.workflowLogsLoadingById[runId]) {
      return 'Loading logs...';
    }
    return this.workflowLogsById[runId] || 'No output available.';
  }

  isWorkflowLogLoading(row: HackathonMyWorkflowRow): boolean {
    const runId = row.id || '';
    return !!runId && !!this.workflowLogsLoadingById[runId];
  }

  isEvaluateDisabledNoPsNotebook(): boolean {
    return this.hasNotebookForCurrentProblemStatement === false;
  }

  isCurrentProblemEvaluateCooldownActive(): boolean {
    if (!this.ps?.id) {
      return false;
    }

    const nextAllowedAtMs = this.getEvaluateCooldownUntilMs(this.ps.id);
    if (!nextAllowedAtMs) {
      return false;
    }
    if (Date.now() >= nextAllowedAtMs) {
      this.clearEvaluateCooldown(this.ps.id);
      return false;
    }
    return true;
  }

  getEvaluateButtonLabel(): string {
    if (this.isEvaluateSubmitting) {
      return 'Submitting...';
    }
    return 'Evaluate';
  }

  getViewEvaluationButtonLabel(): string {
    if (this.isViewEvaluationLoading) {
      return 'Fetching...';
    }
    return 'View my evaluation';
  }

  async goToMyProjectsNotebookListFromRunningModal(): Promise<void> {
    if (this.isNotebookListNavigating || !this.ps?.id) {
      return;
    }

    const problem = this.ps;
    this.isNotebookListNavigating = true;
    try {
      await this.rolesService.getRoles();
      const userRoles = this.rolesService.getUserRoles();
      const panelRoute = this.rolesService.getHighestRolePanelRoute(userRoles || []);

      if (panelRoute.startsWith('/admin')) {
        this.toasterService.warning('You dont have access to create the sandbox');
        return;
      }

      const myProjectsRoute = this.getMyProjectsRouteFromPanel(panelRoute);
      this.resetNotebookRunningGateState();
      await this.router.navigate([myProjectsRoute], {
        queryParams: {
          tab: 'list',
          source: 'hackathon',
          action: 'evaluate',
          psId: problem.id,
          psName: problem.title,
        },
      });
    } catch (error) {
      console.error('Error navigating to notebook list from problem statement:', error);
      this.toasterService.warning('Unable to open My Projects. Please try again.');
    } finally {
      this.isNotebookListNavigating = false;
    }
  }

  closeNotebookRunningModal(): void {
    if (this.isNotebookListNavigating) {
      return;
    }
    this.resetNotebookRunningGateState();
  }

  private getEvaluationPayloadForProblemStatement(
    psId: number,
  ): { challengeName: string; pvc: string } | null {
    const payloadMap: Record<number, { challengeName: string; pvc: string }> = {
      1: {
        challengeName: 'Clinical Document Classification',
        pvc: 'clinical-document-classification-complia-pvc',
      },
      2: {
        challengeName: 'Radiological Image-Based Condition Detection & Report Correlation',
        pvc: 'radiological-image-based-condition-detec-pvc',
      },
      3: {
        challengeName: 'Document Forgery / Deepfake Detection',
        pvc: 'document-forgery-deepfake-detection-pvc',
      },
    };

    return payloadMap[psId] ?? null;
  }

  private persistEvaluateCooldown(psId: number, cooldownUntil: Date): void {
    localStorage.setItem(
      this.getEvaluateCooldownStorageKey(psId),
      cooldownUntil.toISOString(),
    );
  }

  private getEvaluateCooldownUntilMs(psId: number): number | null {
    const rawValue = localStorage.getItem(this.getEvaluateCooldownStorageKey(psId));
    if (!rawValue) {
      return null;
    }
    const parsedMs = Date.parse(rawValue);
    if (Number.isNaN(parsedMs)) {
      this.clearEvaluateCooldown(psId);
      return null;
    }
    return parsedMs;
  }

  private clearEvaluateCooldown(psId: number): void {
    localStorage.removeItem(this.getEvaluateCooldownStorageKey(psId));
  }

  private getEvaluateCooldownStorageKey(psId: number): string {
    return `${this.evaluateCooldownStoragePrefix}${this.getCurrentUserCooldownScope()}:${psId}`;
  }

  private getCurrentUserCooldownScope(): string {
    const keycloakInstance = (this.keycloak as any)?.getKeycloakInstance?.();
    const tokenParsed = keycloakInstance?.tokenParsed as
      | { sub?: string; preferred_username?: string; email?: string }
      | undefined;
    return tokenParsed?.sub || tokenParsed?.preferred_username || tokenParsed?.email || 'anonymous';
  }

  private getCurrentProblemCooldownCountdownLabel(): string | null {
    if (!this.ps?.id) {
      return null;
    }
    const nextAllowedAtMs = this.getEvaluateCooldownUntilMs(this.ps.id);
    if (!nextAllowedAtMs) {
      return null;
    }
    const remainingMs = Math.max(0, nextAllowedAtMs - this.nowMs);
    const totalSeconds = Math.ceil(remainingMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  private startCooldownTicker(): void {
    if (this.cooldownTicker) {
      return;
    }
    this.cooldownTicker = setInterval(() => {
      this.nowMs = Date.now();
    }, 1000);
  }

  private stopCooldownTicker(): void {
    if (!this.cooldownTicker) {
      return;
    }
    clearInterval(this.cooldownTicker);
    this.cooldownTicker = null;
  }

  private resolveCooldownTimestamp(error: HackathonSubmitError): Date | null {
    if (error.nextAllowedAt) {
      const parsedMs = Date.parse(error.nextAllowedAt);
      if (!Number.isNaN(parsedMs)) {
        return new Date(parsedMs);
      }
    }

    const detail = error?.detail || error?.message || '';
    const fallbackMatch = detail.match(
      /next allowed at\s+(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z)/i,
    );
    if (!fallbackMatch?.[1]) {
      return null;
    }

    const parsedMs = Date.parse(fallbackMatch[1]);
    return Number.isNaN(parsedMs) ? null : new Date(parsedMs);
  }

  private getApiErrorMessage(error: HackathonSubmitError, fallback: string): string {
    const detail = (error?.detail || '').trim();
    if (detail) {
      const extractedDetail = this.extractReadableApiMessage(detail);
      return extractedDetail || detail;
    }
    const message = (error?.message || '').trim();
    if (!message) {
      return fallback;
    }
    const normalized = message.replace(/^Hackathon [^:]+:\s*/i, '').trim();
    const extractedMessage = this.extractReadableApiMessage(normalized);
    return extractedMessage || normalized || fallback;
  }

  private extractReadableApiMessage(raw: string): string | null {
    const trimmed = raw.trim();
    if (!trimmed) {
      return null;
    }

    // Handle cases like {"error":"maximum 5 submissions per day reached"}
    if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
      try {
        const parsed = JSON.parse(trimmed) as { error?: unknown; detail?: unknown; message?: unknown };
        const candidate = parsed.error ?? parsed.detail ?? parsed.message;
        if (typeof candidate === 'string' && candidate.trim()) {
          return candidate.trim();
        }
      } catch {
        // fall back to raw text below
      }
    }

    return null;
  }

  private normalizeRunOutput(output: unknown): string {
    if (output === undefined || output === null) {
      return 'No output available.';
    }
    if (typeof output === 'string') {
      return output.trim() || 'No output available.';
    }
    try {
      return JSON.stringify(output, null, 2);
    } catch {
      return String(output);
    }
  }

  private async handleSandboxAction(action: 'sandbox' | 'evaluate'): Promise<void> {
    try {
      const isLoggedIn = await this.keycloak.isLoggedIn();
      if (!isLoggedIn) {
        this.showLoginModal = true;
        return;
      }

      await this.rolesService.getRoles();
      const userRoles = this.rolesService.getUserRoles();
      const panelRoute = this.rolesService.getHighestRolePanelRoute(userRoles || []);

      if (panelRoute.startsWith('/admin')) {
        this.toasterService.warning('You dont have access to create the sandbox');
        return;
      }

      const myProjectsRoute = this.getMyProjectsRouteFromPanel(panelRoute);
      const hasExistingNotebooks = await this.hasExistingNotebooks();
      const tab = hasExistingNotebooks ? 'list' : 'create';

      this.router.navigate([myProjectsRoute], {
        queryParams: {
          tab,
          source: 'hackathon',
          action,
          psId: this.ps?.id ?? undefined,
          psName: this.ps?.title ?? undefined,
        },
      });
    } catch (error) {
      console.error('Error opening sandbox from problem statement detail:', error);
      this.toasterService.warning('Unable to open Sandbox. Please try again.');
    }
  }

  private getMyProjectsRouteFromPanel(panelRoute: string): string {
    if (panelRoute.startsWith('/org-admin')) {
      return '/org-admin/my-projects';
    }

    if (panelRoute.startsWith('/provider')) {
      return '/provider/my-projects';
    }

    if (panelRoute.startsWith('/admin')) {
      return '/admin/project';
    }

    return '/consumer/my-projects';
  }

  private async hasExistingNotebooks(): Promise<boolean> {
    try {
      const response = await this.sandboxApiService.listNotebooks();
      const notebooks = this.extractNotebookItems(response);
      return notebooks.length > 0;
    } catch (error) {
      console.warn('Unable to fetch notebook list. Falling back to list tab.', error);
      // Avoid false negatives: when in doubt, prefer list tab.
      return true;
    }
  }

  private extractNotebookItems(response: any): any[] {
    if (Array.isArray(response)) {
      return response;
    }

    if (response?.result) {
      if (Array.isArray(response.result)) {
        return response.result;
      }
      if (Array.isArray(response.result.results)) {
        return response.result.results;
      }
      if (Array.isArray(response.result.data)) {
        return response.result.data;
      }
      if (Array.isArray(response.result.notebooks)) {
        return response.result.notebooks;
      }
      if (Array.isArray(response.result.notebooks?.notebooks)) {
        return response.result.notebooks.notebooks;
      }
    }

    if (Array.isArray(response?.notebooks)) {
      return response.notebooks;
    }
    if (Array.isArray(response?.notebooks?.notebooks)) {
      return response.notebooks.notebooks;
    }

    if (Array.isArray(response?.data)) {
      return response.data;
    }

    if (Array.isArray(response?.items)) {
      return response.items;
    }

    return [];
  }

  private async ensureNotebookNotRunningForEvaluate(): Promise<boolean> {
    try {
      const response = await this.sandboxApiService.listNotebooks();
      const notebooks = this.extractNotebookItems(response);
      const psId = this.ps?.id;
      if (psId == null) {
        return true;
      }
      const runningNotebook = notebooks.find(
        (item) => this.isNotebookRunning(item) && this.notebookBelongsToProblemStatement(item, psId),
      );
      if (!runningNotebook) {
        return true;
      }

      this.runningNotebookName = this.resolveNotebookName(runningNotebook);
      this.showNotebookRunningModal = true;
      return false;
    } catch (error) {
      console.error('Unable to validate notebook state before evaluate:', error);
      this.toasterService.warning('Unable to verify notebook status. Please try again.');
      return false;
    }
  }

  private resetNotebookRunningGateState(): void {
    this.showNotebookRunningModal = false;
    this.runningNotebookName = '';
  }

  private async refreshNotebookEligibilityForEvaluate(): Promise<void> {
    const psId = this.ps?.id;
    if (psId == null) {
      this.hasNotebookForCurrentProblemStatement = null;
      return;
    }
    try {
      const isLoggedIn = await this.keycloak.isLoggedIn();
      if (!isLoggedIn) {
        this.hasNotebookForCurrentProblemStatement = null;
        return;
      }
      const response = await this.sandboxApiService.listNotebooks();
      const notebooks = this.extractNotebookItems(response);
      this.hasNotebookForCurrentProblemStatement = notebooks.some((item) =>
        this.notebookBelongsToProblemStatement(item, psId),
      );
    } catch (error) {
      console.warn('Unable to refresh notebook eligibility for evaluate:', error);
      this.hasNotebookForCurrentProblemStatement = null;
    }
  }

  /**
   * A notebook belongs to this problem statement when PVC / image / name match
   * the evaluation payload for that PS (same rules as the running-notebook gate).
   */
  private notebookBelongsToProblemStatement(notebook: any, psId: number): boolean {
    const payload = this.getEvaluationPayloadForProblemStatement(psId);
    if (!payload?.pvc) {
      return false;
    }

    const evaluatePvc = payload.pvc.trim().toLowerCase();
    const evalBase = evaluatePvc.replace(/-pvc$/i, '');

    const rawPvc = (notebook?.pvcName ?? notebook?.pvc ?? notebook?.pvc_name ?? '')
      .toString()
      .trim()
      .toLowerCase();
    if (rawPvc) {
      const nbBase = rawPvc.replace(/-pvc$/i, '');
      if (rawPvc === evaluatePvc || nbBase === evalBase) {
        return true;
      }
      if (evalBase.length >= 8 && (rawPvc.includes(evalBase) || nbBase.includes(evalBase))) {
        return true;
      }
    }

    const image = (notebook?.imageName ?? notebook?.image ?? notebook?.image_name ?? '')
      .toString()
      .toLowerCase();
    const marker =
      psId === 1 ? 'nha-ps1' : psId === 2 ? 'nha-ps2' : psId === 3 ? 'nha-ps3' : '';
    if (marker && image.includes(marker)) {
      return true;
    }

    const nbName = (notebook?.name ?? notebook?.notebookName ?? '').toString().trim().toLowerCase();
    if (nbName && evalBase) {
      if (nbName === evalBase) {
        return true;
      }
      if (evalBase.startsWith(nbName)) {
        return true;
      }
    }

    return false;
  }

  private isNotebookRunning(notebook: any): boolean {
    const status = (notebook?.status || notebook?.state || notebook?.phase || '').toString().trim().toLowerCase();
    return status === 'running';
  }

  private resolveNotebookName(notebook: any): string {
    const candidate =
      notebook?.notebookName ||
      notebook?.name ||
      notebook?.notebook_name ||
      notebook?.display_name ||
      notebook?.id ||
      '';
    const name = String(candidate || '').trim();
    return name || 'the running notebook';
  }

  difficultyClass(level: string): string {
    return (
      { Beginner: 'tag--beginner', Intermediate: 'tag--intermediate', Advanced: 'tag--advanced' }[level] ?? ''
    );
  }

  /**
   * Output/evaluation guidelines — used only by "View guidelines".
   * Not the STG resource bundles (those match Knowledge hub paths below).
   */
  get outputGuidelinesFileUrl(): string | null {
    if (!this.ps?.id) {
      return null;
    }
    const fileMap: Record<number, string> = {
      1: '/NHA/ps-1.pdf',
      2: '/NHA/ps-2.zip',
      3: '/NHA/PS3_guidelines.pdf',
    };
    return fileMap[this.ps.id] ?? null;
  }

  /** STG zip bundles — same assets as Knowledge hub → Resources tab */
  get problemStatementResources(): ProblemStatementResourceItem[] {
    if (!this.ps?.id) {
      return [];
    }
    const stgBundleMap: Record<number, string> = {
      1: '/documents/NHA/STGs_ps1.zip',
      2: '/documents/NHA/STGs_ps2.zip',
    };
    const fileUrl = stgBundleMap[this.ps.id];
    if (!fileUrl) {
      return [];
    }

    return [
      {
        label: `${this.ps?.tag || 'PS'} stg bundle`,
        description: `Download resources for ${this.ps?.tag || 'this problem statement'}.`,
        fileUrl,
      },
    ];
  }

  async downloadProblemStatementResource(resource: ProblemStatementResourceItem): Promise<void> {
    await this.downloadFile(resource.fileUrl, resource.label);
  }

  async downloadOutputGuidelines(): Promise<void> {
    const fileUrl = this.outputGuidelinesFileUrl;
    if (!fileUrl) {
      this.toasterService.warning('Output guidelines are not available for this problem statement.');
      return;
    }

    await this.downloadFile(fileUrl, 'output guidelines');
  }

  private async downloadFile(fileUrl: string, fileLabel: string): Promise<void> {
    try {
      const response = await fetch(fileUrl, { cache: 'no-store' });
      if (!response.ok) {
        throw new Error(`Failed to download file. Status: ${response.status}`);
      }

      const blob = await response.blob();
      if (!blob || blob.size === 0) {
        throw new Error('Downloaded file is empty.');
      }

      const objectUrl = URL.createObjectURL(blob);
      const fileName = fileUrl.split('/').pop() ?? 'output-guidelines';
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = fileName;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error('Error downloading resource file:', error);
      this.toasterService.error(`Unable to download ${fileLabel}. Please try again.`);
    }
  }
}
